<template>
  <div
    class="indicator-selector"
    :class="{
      open: isOpen,
      empty: indName == null,
      disabled: !editable,
      interactive: indName !== null ? breakdownDimensions.length : true,
      'is-mobile': isMobile,
      'is-loading': isLoading,
    }"
  >
    <div class="indicator-selector__wrapper">
      <a
        v-if="editable && !required && indName"
        class="indicator-selector__reset-btn"
        @click="resetSelector"
      />
      <div class="indicator-selector__indicator" @click="onOpen($event)">
        <div ref="indSelector" class="indicator-selector__label">{{ axis }}</div>
        <div class="indicator-selector__name" :class="{ ready: Boolean(indName) }">
          {{ indName || 'Select indicator' }}
        </div>
        <div v-if="breakdownDimensions.length > 0" class="expand" />
      </div>
      <div class="indicator-selector__breakdowns" :cols="visibleBreakdownDimensions.length">
        <div
          v-for="dim in visibleBreakdownDimensions"
          :key="dim.id"
          class="indicator-selector__breakdown-dimension"
        >
          <breakdown-selector
            :id="dim.id"
            :name="dim.name[lng]"
            :selected="breakdownSelection[dim.id]"
            :options="dim.options"
            :disabled="isPrintable"
            :is-wrapped="true"
            :is-loading="isLoading"
            @update="updateSelection($event)"
          />
        </div>
        <div v-if="showUnits && unit" class="indicator-selector__units">
          <unit-selector v-model="unit" :selection="unit" :values="units" data-align="bottom" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BreakdownSelector from './BreakdownSelector.vue'
import UnitSelector from './UnitSelector.vue'

export default {
  components: { BreakdownSelector, UnitSelector },
  model: {
    prop: 'open',
    event: 'open',
  },
  props: [
    'open',
    'appSlot',
    'breakdownSelection',
    'breakdownDimensions',
    'units',
    'editable',
    'required',
    'isLoading',
  ],
  computed: {
    isOpen: {
      get() {
        return this.open === this.appSlot
      },
      set(to) {
        this.$emit('open', to && this.appSlot)
      },
    },
    unit: {
      get() {
        const unitId = this.$store.state.query[this.appSlot + 'Unit']
        return this.units.find(u => u.id === unitId)
      },
      set(selection) {
        const value = selection[0]
        this.$store.commit(this.appSlot + 'Unit', value.id)
      },
    },
    isNDSC() {
      return /DF_NDSC$/i.test(this.$store.state.query[this.appSlot + 'CubeId'])
    },
    axis() {
      return this.appSlot === 'main' ? 'x axis' : this.appSlot === 'pair' ? 'y axis' : 'size'
    },
    indId() {
      return this.$store.state.query[this.appSlot + 'IndId']
    },
    indName() {
      return this.indId
        ? this.$store.getters.indicatorName(this.appSlot, this.indId, this.lng)
        : null
    },
    visibleBreakdownDimensions() {
      const dimensions = this.breakdownDimensions.filter(d => !d.notApplicable)

      if (this.isNDSC) {
        return dimensions.filter(dim => dim.id !== 'CL_GD_BREAKDOWNS_21_POLICY')
      }

      return dimensions
    },
    showUnits() {
      return this.units && this.units.length > 1
    },
  },
  watch: {
    isOpen(to) {
      if (to) {
        document.body.addEventListener('click', this.onClickOut)
      } else {
        document.body.removeEventListener('click', this.onClickOut)
      }
    },
  },
  mounted() {
    const offset = this.$el.getBoundingClientRect()
    for (let dropdown of this.$el.querySelectorAll('.dropdown-select__options')) {
      dropdown.style.maxHeight =
        window.innerHeight - (offset.top + offset.height) - window.innerHeight * 0.1 + 'px'
    }
  },
  methods: {
    onClickOut(ev) {
      if (this.$el.contains(ev.target)) return
      this.isOpen = false
    },
    onOpen(ev) {
      if (ev.target === this.$refs.indSelector || this.indName === null) {
        this.editable && this.$root.$emit('sidebar:open', this.appSlot)
      } else {
        let isOpen = !this.isOpen
        isOpen &= this.breakdownDimensions.length > 0
        isOpen &= !this.isPrintable
        this.isOpen = Boolean(isOpen)
      }
    },
    updateSelection(value) {
      this.$emit('update', {
        slot: this.appSlot,
        value: { ...this.breakdownSelection, ...value },
      })
      this.$emit('open', null)
    },
    resetSelector() {
      this.$store.commit(this.appSlot + 'CubeId', null)
      if (this.isOpen) this.$emit('open', null)
    },
  },
}
</script>

<style lang="stylus">
.indicator-selector
  border: 1px solid transparent
  cursor: pointer
  position: relative
  border-radius: 15px
  transition: border-color 200ms ease-in

  &.empty
    .indicator-selector__indicator
      color: #acacac

    .indicator-selector__indicator .expand,
    &:hover .indicator-selector__reset-btn
      display: none

  &.open
    &.is-mobile .indicator-selector__reset-btn
      display: block

    .indicator-selector__indicator
      .expand
        transform: translateY(-50%) rotate(180deg)

    .indicator-selector__breakdowns
      display: flex
      flex-direction: column

  &:hover:not(.is-mobile) .indicator-selector__reset-btn
    display: block

  &:not(.interactive)
    cursor: default

.indicator-selector__reset-btn
  m-icon("close", 20)
  bottom: 0
  cursor: pointer
  display: none
  right: vw(-20px)
  margin: auto
  position: absolute
  top: 0

  +m-breakpoint(sm md)
    left: vw(-2px)

.indicator-selector__indicator
  align-items: center
  display: flex
  margin-left: vw(20px)
  padding: vw(12px) vw(20px) 0 vw(70px)
  position: relative

  .mobile &
    margin-left: 0
    overflow: hidden
    padding: vw(9px) 0 0 vw(40px)

    +m-breakpoint(landscape)
      padding: vh(10px) 0 0 vw(40px)

  .indicator-selector__label
    m-font-size(11, 16)
    border: 1px solid $blue-2
    border-radius: vw(22px)
    color: $blue-2
    height: 80%
    text-align: center
    text-transform: capitalize
    position: absolute
    cursor: pointer
    width: vw(60px)
    left: 0px
    top: 10%
    display: flex
    align-items: center
    justify-content: center
    line-height: 1

    .disabled &
      cursor: default

    &:hover
      background-color: rgba($havelock-blue, 0.1)

    .disabled &
    .mobile &
      box-shadow: none
      padding: vw(2px) vw(4px)

    .mobile &
      m-font-size(9, 14)
      height: 60%
      top: 20%
      width: vw(40px)

      +m-breakpoint(landscape)
        font-size: 0.5em
        line-height: 1.5
        width: vw(30px)
        margin-right: auto

    .disabled &
      border: none
      color: #fff
      background-color: $blue-2

  .indicator-selector__name
    m-ellipsis(100%)
    flex: 1 1 0%
    padding-right: vw(30px)
    margin-top: -0.025em
    padding-bottom: vw(12px)
    border-bottom: 1px solid $gray

    .open &
      border-bottom: none

    &.ready
      color: $black

    .mobile &
      padding-bottom: vw(9px)
      m-font-size(11, 14)
      margin-left: vw(5px)
      padding-right: vw(25px)

      +m-breakpoint(landscape)
        padding-bottom: vh(10px)
        font-size: 0.7em
        line-height: 1.1
        margin-left: auto


  .expand
    m-icon("arrow-down", 25)
    bottom: 0
    position: absolute
    right: vw(20px)
    top: 50%
    transform: translateY(-50%)
    transition: transform 0.25s ease-in-out

    .disabled:not(.interactive) &
      display: none

    .mobile &
      right: 0

      +m-breakpoint(landscape)
        m-icon("arrow-down", 14)

.indicator-selector__wrapper
  position: relative

.indicator-selector__breakdowns
  background-color: #fff
  border: 1px solid $gray
  border-radius: 15px
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.05)
  display: none
  left: 0
  bottom: 0
  transform: translateY(100%)
  padding: vw(10px) vw(20px)
  position: absolute
  width: "calc(100% - %s)" % (vw(40px))
  margin-left: vw(20px)
  z-index: 10

  .mobile &
    width: 100%
    padding: vw(4px) vw(8px)
    margin-left: 0

    .dropdown-select__label
      padding-right: vw(10px)

      > .value
        margin-right: 1.5em

.indicator-selector__units
  border-top: 1px solid #ddd
  padding-top: 0.4em

.indicator-selector__breakdown-dimension
  display: inline-block

  .breakdown-selector .dropdown-select__label
    border-bottom: none
</style>
